import { useQuery } from "@tanstack/react-query";
import { useRoute } from "wouter";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { Skeleton } from "@/components/ui/skeleton";
import type { SitePage } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";

export default function SitePage() {
  const [, params] = useRoute("/page/:slug");
  const slug = params?.slug;

  const { data: page, isLoading } = useQuery<SitePage>({
    queryKey: [`/api/site-pages/${slug}`],
    queryFn: async () => {
      const res = await fetch(`/api/site-pages/${slug}`);
      if (!res.ok) throw new Error("Failed to fetch page");
      return res.json();
    }
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#030308] flex flex-col">
        <SiteNav />
        <main className="flex-1 py-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <Skeleton className="h-12 w-1/2 mb-8 bg-white/5" />
            <Skeleton className="h-4 w-full mb-4 bg-white/5" />
            <Skeleton className="h-4 w-full mb-4 bg-white/5" />
            <Skeleton className="h-4 w-3/4 mb-4 bg-white/5" />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen bg-[#030308] flex flex-col">
        <SiteNav />
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-4">Page Not Found</h1>
            <Link href="/">
              <Button variant="outline" className="border-white/10 text-white hover:bg-white/10">
                Back Home
              </Button>
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030308] flex flex-col">
      <SiteNav />
      
      <main className="flex-1 py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <header className="mb-12">
            <h1 className="text-4xl md:text-6xl font-display font-black text-white mb-6 tracking-tight" data-testid="text-page-title">
              {page.title}
            </h1>
            {page.metaDescription && (
              <p className="text-xl text-white/40 leading-relaxed max-w-2xl">
                {page.metaDescription}
              </p>
            )}
          </header>

          <div 
            className="prose prose-invert prose-blue max-w-none prose-headings:font-display prose-headings:font-black prose-p:text-white/70 prose-p:leading-relaxed prose-li:text-white/70 prose-strong:text-white"
            data-testid="text-page-content"
          >
            <div style={{ whiteSpace: 'pre-wrap' }}>
              {page.content}
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
